import axios from 'axios';

// Configuration de base pour l'API
import API_BASE_URL from '../config/api';

// Créer une instance axios avec configuration par défaut
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Messages génériques et compréhensibles, utilisés quand le backend ne renvoie pas de `detail`
// exploitable (sinon error.message affiche le texte brut d'axios, ex: "Request failed with status code 401")
const FRIENDLY_STATUS_MESSAGES = {
  401: 'Votre session a expiré. Veuillez vous reconnecter.',
  403: "Vous n'avez pas les droits nécessaires pour cette action.",
  404: 'Ressource introuvable.',
  500: 'Erreur serveur. Veuillez réessayer plus tard.',
};

// Intercepteur pour gérer les erreurs de réponse
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/business/login';
    }
    if (!error.response?.data?.detail) {
      error.message = FRIENDLY_STATUS_MESSAGES[error.response?.status] || 'Une erreur est survenue. Veuillez réessayer.';
    }
    return Promise.reject(error);
  }
);

// Service API pour la gestion des restaurants
export const restaurantManagementApi = {
  // === PROFIL DU RESTAURANT ===
  
  // Récupérer le profil du restaurant
  getRestaurantProfile: async () => {
    try {
      const response = await apiClient.get('/restaurant/profile/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération du profil');
    }
  },

  // Mettre à jour le profil du restaurant
  updateRestaurantProfile: async (profileData) => {
    try {
      const response = await apiClient.put('/restaurant/profile/', profileData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour du profil');
    }
  },

  // === HORAIRES D'OUVERTURE ===
  
  // Récupérer les horaires d'ouverture
  getBusinessHours: async () => {
    try {
      const response = await apiClient.get('/restaurant/business-hours/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des horaires:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des horaires');
    }
  },

  // Mettre à jour les horaires d'ouverture
  updateBusinessHours: async (hoursData) => {
    try {
      const response = await apiClient.put('/restaurant/business-hours/', hoursData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des horaires:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour des horaires');
    }
  },

  // === PARAMÈTRES DE LIVRAISON ===
  
  // Récupérer les paramètres de livraison
  getDeliverySettings: async () => {
    try {
      const response = await apiClient.get('/restaurant/delivery-settings/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres de livraison:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des paramètres de livraison');
    }
  },

  // Mettre à jour les paramètres de livraison
  updateDeliverySettings: async (settingsData) => {
    try {
      const response = await apiClient.put('/restaurant/delivery-settings/', settingsData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des paramètres de livraison:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour des paramètres de livraison');
    }
  },

  // === GESTION DES IMAGES ===
  
  // Upload d'image
  uploadImage: async (formData) => {
    try {
      const response = await apiClient.post('/restaurant/upload-image/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'upload de l\'image:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de l\'upload de l\'image');
    }
  },

  // Supprimer une image
  deleteImage: async (imageType) => {
    try {
      const response = await apiClient.delete(`/restaurant/images/${imageType}/`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'image:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la suppression de l\'image');
    }
  },

  // === GESTION DES AVIS ===
  
  // Récupérer les avis
  getReviews: async (params = {}) => {
    try {
      const response = await apiClient.get('/restaurant/reviews/', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des avis:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des avis');
    }
  },

  // Répondre à un avis
  replyToReview: async (reviewId, replyText) => {
    try {
      const response = await apiClient.post(`/restaurant/reviews/${reviewId}/reply/`, {
        reply: replyText
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la réponse:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la réponse');
    }
  },

  // Signaler un avis
  reportReview: async (reviewId) => {
    try {
      const response = await apiClient.post(`/restaurant/reviews/${reviewId}/report/`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du signalement:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors du signalement de l\'avis');
    }
  },

  // === GESTION DU MENU ===
  
  // Récupérer les plats du restaurant
  getDishes: async (params = {}) => {
    try {
      const response = await apiClient.get('/restaurant/dishes/', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des plats:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des plats');
    }
  },

  // Créer un nouveau plat
  createDish: async (dishData) => {
    try {
      const response = await apiClient.post('/restaurant/dishes/', dishData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du plat:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la création du plat');
    }
  },

  // Mettre à jour un plat
  updateDish: async (dishId, dishData) => {
    try {
      const response = await apiClient.put(`/restaurant/dishes/${dishId}/`, dishData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du plat:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour du plat');
    }
  },

  // Supprimer un plat
  deleteDish: async (dishId) => {
    try {
      const response = await apiClient.delete(`/restaurant/dishes/${dishId}/`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du plat:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la suppression du plat');
    }
  },

  // === GESTION DES COMMANDES ===
  
  // Récupérer les commandes
  getOrders: async (params = {}) => {
    try {
      const response = await apiClient.get('/restaurant/orders/', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des commandes');
    }
  },

  // Mettre à jour le statut d'une commande
  updateOrderStatus: async (orderId, status) => {
    try {
      const response = await apiClient.patch(`/restaurant/orders/${orderId}/`, {
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour du statut');
    }
  },

  // === ANALYTIQUES ===
  
  // Récupérer les statistiques du restaurant
  getAnalytics: async (period = 'month') => {
    try {
      const response = await apiClient.get(`/restaurant/analytics/?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des statistiques');
    }
  },

  // === GESTION DES EMPLOYÉS ===
  
  // Récupérer les employés
  getEmployees: async () => {
    try {
      const response = await apiClient.get('/restaurant/employees/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des employés:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des employés');
    }
  },

  // Créer un employé
  createEmployee: async (employeeData) => {
    try {
      const response = await apiClient.post('/restaurant/employees/', employeeData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'employé:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la création de l\'employé');
    }
  },

  // Mettre à jour un employé
  updateEmployee: async (employeeId, employeeData) => {
    try {
      const response = await apiClient.put(`/restaurant/employees/${employeeId}/`, employeeData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'employé:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour de l\'employé');
    }
  },

  // Supprimer un employé
  deleteEmployee: async (employeeId) => {
    try {
      const response = await apiClient.delete(`/restaurant/employees/${employeeId}/`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'employé:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la suppression de l\'employé');
    }
  },

  // === GESTION DES PROMOTIONS ===
  
  // Récupérer les promotions
  getPromotions: async () => {
    try {
      const response = await apiClient.get('/restaurant/promotions/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des promotions:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des promotions');
    }
  },

  // Créer une promotion
  createPromotion: async (promotionData) => {
    try {
      const response = await apiClient.post('/restaurant/promotions/', promotionData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la promotion:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la création de la promotion');
    }
  },

  // Mettre à jour une promotion
  updatePromotion: async (promotionId, promotionData) => {
    try {
      const response = await apiClient.put(`/restaurant/promotions/${promotionId}/`, promotionData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la promotion:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour de la promotion');
    }
  },

  // Supprimer une promotion
  deletePromotion: async (promotionId) => {
    try {
      const response = await apiClient.delete(`/restaurant/promotions/${promotionId}/`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de la promotion:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la suppression de la promotion');
    }
  },

  // === GESTION DES SERVICES DE TRAITEUR ===
  
  // Récupérer les services de traiteur
  getCateringServices: async () => {
    try {
      const response = await apiClient.get('/restaurant/catering-services/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des services de traiteur:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des services de traiteur');
    }
  },

  // Créer un service de traiteur
  createCateringService: async (serviceData) => {
    try {
      const response = await apiClient.post('/restaurant/catering-services/', serviceData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du service de traiteur:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la création du service de traiteur');
    }
  },

  // Mettre à jour un service de traiteur
  updateCateringService: async (serviceId, serviceData) => {
    try {
      const response = await apiClient.put(`/restaurant/catering-services/${serviceId}/`, serviceData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du service de traiteur:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour du service de traiteur');
    }
  },

  // Supprimer un service de traiteur
  deleteCateringService: async (serviceId) => {
    try {
      const response = await apiClient.delete(`/restaurant/catering-services/${serviceId}/`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du service de traiteur:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la suppression du service de traiteur');
    }
  },

  // === GESTION DU PORTEFEUILLE ===
  
  // Récupérer les informations du portefeuille
  getWalletInfo: async () => {
    try {
      const response = await apiClient.get('/restaurant/wallet/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des informations du portefeuille:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des informations du portefeuille');
    }
  },

  // Récupérer l'historique des transactions
  getTransactionHistory: async (params = {}) => {
    try {
      const response = await apiClient.get('/restaurant/wallet/transactions/', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique des transactions:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération de l\'historique des transactions');
    }
  },

  // Demander un retrait
  requestWithdrawal: async (amount, bankDetails) => {
    try {
      const response = await apiClient.post('/restaurant/wallet/withdraw/', {
        amount,
        bank_details: bankDetails
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la demande de retrait:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de la demande de retrait');
    }
  },
};

export default restaurantManagementApi;
